import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { mediaUrl, apiErr } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import ImageUploader from "@/components/admin/ImageUploader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const blank = () => ({ title: "", slug: "", description: "", image: "", hero_image: "", order: 0, active: true });

export default function AdminCategories() {
  const { user } = useAuth();
  const isManager = user.role === "store_manager";
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank());

  const load = () => api.get("/categories").then(({ data }) => setCats(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(blank()); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setF({ ...blank(), ...c }); setOpen(true); };

  const save = async () => {
    const payload = { ...f, slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") };
    try {
      if (editing) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Saved."); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (c) => { if (!window.confirm(`Delete ${c.title}?`)) return; try { await api.delete(`/categories/${c.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="admin-categories">
      <div className="flex justify-between items-center mb-8">
        <div><h1 className="font-heading text-4xl tracking-tight">Categories</h1><p className="font-body text-sm text-ink/60 mt-1">{isManager ? "Update category imagery." : "Create, edit and reorder collection categories."}</p></div>
        {!isManager && <button onClick={openNew} className="adm-btn" data-testid="create-category-btn"><Plus size={14} /> Add Category</button>}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {cats.map((c) => (
          <div key={c.id} className="adm-card" data-testid={`category-row-${c.slug}`}>
            <div className="aspect-[3/2] bg-cream overflow-hidden mb-3"><img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" /></div>
            <div className="flex justify-between items-center">
              <div><p className="font-heading text-xl">{c.title}</p><p className="font-body text-xs text-ink/50">/{c.slug}</p></div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(c)} className="icon-thin text-ink/60 hover:text-ink" data-testid={`edit-category-${c.slug}`}><Pencil size={15} /></button>
                {!isManager && <button onClick={() => del(c)} className="icon-thin text-ink/60 hover:text-destructive"><Trash2 size={15} /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Category" : "New Category"} testId="category-modal">
        <div className="space-y-4">
          {!isManager && (
            <>
              <div><label className="adm-label">Title</label><input className="adm-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="category-title-input" /></div>
              <div><label className="adm-label">Slug (auto if empty)</label><input className="adm-input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></div>
              <div><label className="adm-label">Description</label><input className="adm-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
            </>
          )}
          <ImageUploader value={f.image} onChange={(url) => setF({ ...f, image: url, hero_image: url })} label="Category Card Image" recommended="800×1067 · 3:4" testId="category-image-uploader" />
          <button onClick={save} className="adm-btn w-full" data-testid="category-save-btn">Save Category</button>
        </div>
      </Modal>
    </div>
  );
}
